import React, { createRef, Suspense } from "react";
import BaseElem from "BaseElem";
import "Common.css";
import "./CalendarView.css";
import Button from "@material-ui/core/Button";
import Calendar from "./Calendar";
import {
  dateAdd,
  getMonths,
  firstDayOfWeek,
  getShortMonths,
  formatDateDDMMM,
  parseDate,
  newDate,
} from "Util/DateUtils";
import { getCRShortVisits, getCRFullVisits } from "Actions/VisitActions";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import {
  MSG_WEEK_CHANGE,
  MSG_DATE_CHANGE,
  MSG_EXPAND_COLLAPSE,
  MSG_ON_PAGE_FOCUS,
  MSG_FETCH_VISITS,
  MSG_SHOW_SNACKBAR,
  UNDEF,
  MSG_REFRESH_CR_INFO,
  API_UPDATE_DRAFTS_GET,
  API_PENDING_PAYMENT_DRAFTS_GET,
  c7C7C7C,
} from "Constants";
import { LoadingSpinner } from "Components/CommonComponents";
import Api, { showLoading, showSnackbar } from "Api";
import {
  getVisitDrafts,
  DRAFT_TYPE_NEW,
  DRAFT_TYPE_MOBILE,
  getMobileVisitDrafts,
  getEntVisitDrafts,
  DRAFT_TYPE_UPDATE,
  DRAFT_TYPE_ENTERPRISE,
  DRAFT_TYPE_PENDING_PAYMENT,
} from "Actions/VisitDraftActions";
import { DeeplinkParams } from "Screens/ManageCareRecipientB2C/ManageCareRecipient";
import { Div } from "Components/GenericView";
import { eatClick } from "Util/Utilities";
const BigCalendar = React.lazy(() => import("./BigCalendar"));

type CalendarViewProps = {
  visitStore?: any;
  draftsCount?: number;
  mobileDraftsCount?: number;
  entDraftsCount?: number;
  updateDrafts?: any[];
  pendingPaymentDrafts?: any[];
  onShowListView?: (
    draftsType: string,
    draftsData?: any,
    draftsId?: string
  ) => void;

  // cancel/update/update-pricing
  deeplinkParams?: DeeplinkParams;
};

type CalendarViewState = {
  title: string;
  date: Date;
};

type CalendarViewConnectProps = {
  getVisitDrafts: (_id: string) => void;
  getMobVisitDrafts: (_id: string, onSuccess: any) => void;
  getEntVisitDrafts: (_id: string, onSuccess: any) => void;
  getCRShortVisits: (_id: string, startDate: Date, endDate: Date) => void;
  getCRFullVisits: (_id: string, startDate: Date, endDate: Date) => void;
};

type CalendarViewActualProps = CalendarViewConnectProps &
  CalendarViewProps &
  RouteComponentProps<any>;

class CalendarView extends BaseElem<
  CalendarViewActualProps,
  CalendarViewState
> {
  _reqInterval = 0;
  // @ts-ignore
  _refCalendar = createRef<Calendar>();
  _refExpandToggle = createRef<HTMLDivElement>();
  _refLeftbar = createRef<HTMLDivElement>();
  _refStickyBar = createRef<HTMLDivElement>();
  _refBigCalendar = createRef<any>();

  constructor(props: CalendarViewActualProps) {
    super(props);
    const self = this;
    const date = newDate();

    //TODO: remove
    // date.setDate(date.getDate() - 7);

    self.state = {
      title: self._getTitle(date),
      date: date,
    };
  }

  // Setup event listeners
  _enableListeners(isEnabled = false) {
    const self = this;
    !isEnabled && self.cancelAsync(self._reqInterval);
    self.setListeners(isEnabled, MSG_FETCH_VISITS, MSG_ON_PAGE_FOCUS);
  }

  componentDidMount() {
    const self = this;
    self._enableListeners(true);
    self._refreshData(false);
    self._processDeeplink(self.props.deeplinkParams);
  }

  componentWillUnmount() {
    this._enableListeners(false);
  }

  // Overridden function, refer to BaseElem.tsx
  onMessage(messageName: string, payload?: any) {
    const self = this;
    if (messageName === MSG_FETCH_VISITS && payload) {
      showLoading() &&
        self.async(() => showLoading(false), payload.spinnerDuration || 4000);

      payload.date
        ? self.async(() => self._setDate(payload.date), 200)
        : self._fetchCalendarData(self.state.date);

      self.post(MSG_REFRESH_CR_INFO);

      payload.message &&
        self.post(MSG_SHOW_SNACKBAR, {
          key: new Date().getTime(),
          message: payload.message,
          duration: 5000,
          variant: "success",
        });
    } else if (messageName === MSG_ON_PAGE_FOCUS && self._reqInterval === 0) {
      self._refreshData();
    }
  }

  componentDidUpdate(oldProps: CalendarViewProps) {
    const self = this;
    const props = self.props;
    const params = props.deeplinkParams;
    if (oldProps.deeplinkParams !== params) {
      self._processDeeplink(params);
    }
  }

  _processDeeplink(params?: DeeplinkParams) {
    if (!params || params.consumed) return;

    const self = this;
    const visits = params.visits;
    if (visits && visits[0]) {
      const schedule = visits[0].visit_schedule;
      const date = parseDate(schedule.date_from);
      date && self._setDate(date);
      self._openVisitDialog(params);
    }
  }

  // auto open visit dialog with params
  _openVisitDialog(params: DeeplinkParams, attempt = 1) {
    const self = this;
    const bigCalRef = self._refBigCalendar;
    const current = bigCalRef.current;
    if (current) {
      current.openVisitDialog(params);
    } else if (attempt < 30 /* 3 sec */) {
      self.async(() => {
        self._openVisitDialog(params, attempt + 1);
      }, 100);
    }
  }

  _refreshData(fetchCrInfo = true) {
    const self = this,
      state = self.state;

    // get short + full visits data
    self._fetchCalendarData(state.date);

    // update cr details/past/upcoming numbers and visit drafts
    fetchCrInfo && self.post(MSG_REFRESH_CR_INFO);

    // schedule data refresh timer (2 minutes)
    self.cancelAsync(self._reqInterval);
    self._reqInterval = self.async(() => {
      if (self.isPageVisible) {
        self._refreshData(true);
      } else {
        self._reqInterval = 0;
      }
    }, 120000);
  }

  _onPrevWeek = () => {
    this._setDate(UNDEF, -7);
    this.post(MSG_WEEK_CHANGE, true);
  };

  _onNextWeek = () => {
    this._setDate(UNDEF, 7);
    this.post(MSG_WEEK_CHANGE, false);
  };

  _onTapToday = () => {
    const self = this;
    self._setDate(newDate());
    self._refCalendar.current?.resetCalendar();
  };

  _setDate(date?: Date, daysToAdd = 0) {
    const self = this;
    const validDate = date || self.state.date || newDate();
    let selectedDate = dateAdd(validDate, daysToAdd);
    if (selectedDate) {
      self._fetchCalendarData(selectedDate);
      self.setState({ date: selectedDate });
      self._updateBigCalendar(selectedDate);
    }
  }

  _updateBigCalendar(date: Date) {
    this.post(MSG_DATE_CHANGE, date);
    this.setState({ title: this._getTitle(date), date });
  }

  _getTitle(date: any) {
    var weekStart = new Date(date);
    while (weekStart.getDay() !== firstDayOfWeek()) {
      weekStart.setDate(weekStart.getDate() - 1);
    }
    let weekEnd = dateAdd(weekStart, 6);
    if (weekEnd == null) return "";

    let months = getMonths(),
      shortMonths = getShortMonths();
    let month1 = weekStart.getMonth();
    let month2 = weekEnd.getMonth();
    let fullYear1 = weekStart.getFullYear();
    let fullYear2 = weekEnd.getFullYear();

    if (month1 !== month2) {
      let year1 = fullYear1 !== fullYear2 ? ` ${fullYear1}` : "";
      return `${shortMonths[month1]}${year1} - ${shortMonths[month2]} ${fullYear2}`;
    }

    return months[month1] + " " + fullYear1;
  }

  // callback from mini calendar
  onDateChange = (newDate: Date) => {
    this._fetchCalendarData(newDate);
    this._updateBigCalendar(newDate); //this also updates the state.date
  };

  _getCalendarDots(visitStore?: any) {
    if (!visitStore || !visitStore.visits) return;
    const dots = visitStore.visits.map((v: any) => v.startDate);
    return dots;
  }

  _toggleExpand = () => {
    const self = this,
      nil = null,
      leftBar = self._refLeftbar.current,
      expandToggle = self._refExpandToggle.current,
      attribName = "collapsed";

    self.toggleAttrib(attribName, nil, expandToggle);
    self.toggleAttrib(attribName, nil, leftBar);

    self.post(MSG_EXPAND_COLLAPSE);
  };

  _onScroll = () => {
    const self = this;
    const leftBar = self._refLeftbar.current;
    const innerStickyBar = self._refStickyBar.current;
    if (!leftBar || !innerStickyBar) return;

    const boundingBox = leftBar.getBoundingClientRect();

    const stickyStyle = innerStickyBar.style as any;
    const shouldStick = boundingBox.top < 0;

    stickyStyle.position = shouldStick ? "fixed" : "relative";
    stickyStyle.top = shouldStick ? "16px" : "";
  };

  _fetchCalendarData(d: Date) {
    //console.log('fetching calendar data: ' + d);

    const self = this,
      props = self.props;
    const startDate = dateAdd(d, -30);
    const endDate = dateAdd(d, 30);

    if (!startDate || !endDate) return;
    const crId = props.match.params.id;

    const sDate = new Date(startDate);
    const eDate = new Date(endDate);

    // setDate 1 and 31 is to show little dots on the whole month
    sDate.setDate(1);
    eDate.setDate(31);
    props.getCRShortVisits(crId, sDate, eDate);

    startDate.setDate(d.getDate() - 7);
    endDate.setDate(d.getDate() + 7);

    props.getCRFullVisits(crId, startDate, endDate);
  }
  _onCalendarMonthChange = (montStartDate: Date) => {
    const self = this,
      props = self.props;
    const currentDate = self.state.date;
    const minStart = dateAdd(currentDate, -30);
    const maxEnd = dateAdd(currentDate, 30);

    if (!montStartDate || !minStart || !maxEnd) return;

    const monthStartTime = montStartDate.getTime();
    let start = monthStartTime < minStart.getTime() ? montStartDate : minStart;
    let end = monthStartTime > maxEnd.getTime() ? montStartDate : maxEnd;

    // setDate 1 and 31 is to show little dots on the whole month
    start.setDate(1);
    end.setDate(31);

    props.getCRShortVisits(props.match.params.id, start, end);
  };

  _showMobileDrafts = () => {
    const self = this,
      props = self.props;
    props.getMobVisitDrafts(props.match.params.id, () => {
      props.onShowListView && props.onShowListView(DRAFT_TYPE_MOBILE);
    });
  };
  _showEnterpriseDrafts = () => {
    const self = this,
      props = self.props;
    props.getEntVisitDrafts(props.match.params.id, () => {
      props.onShowListView && props.onShowListView(DRAFT_TYPE_ENTERPRISE);
    });
  };
  _showPendingPaymentDrafts = (draftsId: string) => {
    const self = this,
      props = self.props;
    const crId = props.match.params.id;

    Api.GET(API_PENDING_PAYMENT_DRAFTS_GET + crId + "/" + draftsId, (resp) => {
      const data = resp && resp.data && resp.data.data;
      if (data && props.onShowListView) {
        props.onShowListView(DRAFT_TYPE_PENDING_PAYMENT, data, draftsId);
      }
    });
  };

  _copyWeblink = (link: string) => {
    navigator.clipboard.writeText(link);
    showSnackbar("Payment link copied to clipboard", "success", 3000);
  };

  _resumeUpdateVisitDrafts(draftId: string) {
    const props = this.props;
    Api.GET(API_UPDATE_DRAFTS_GET + draftId, (resp) => {
      const data = resp && resp.data;
      if (data && props.onShowListView) {
        //TESTING
        //data.conditions_changed = false;
        props.onShowListView(DRAFT_TYPE_UPDATE, data);
      }
    });
  }

  render() {
    const self = this,
      state = self.state,
      props = self.props;
    const haveVisitDrafts = (props.draftsCount || 0) > 0;
    const haveMobileDrafts = (props.mobileDraftsCount || 0) > 0;
    const haveEntDrafts = (props.entDraftsCount || 0) > 0;
    const updateDrafts = props.updateDrafts;
    const haveUpdateDrafts = !!(updateDrafts && updateDrafts.length);

    const ppDrafts = props.pendingPaymentDrafts;
    const havePpDrafts = !!(ppDrafts && ppDrafts[0]);

    const haveDrafts =
      haveVisitDrafts ||
      haveMobileDrafts ||
      haveEntDrafts ||
      haveUpdateDrafts ||
      havePpDrafts;

    return (
      <div className="calendar-view horizontal no-select">
        <div className="cv-left-bar" ref={self._refLeftbar}>
          <div className="cv-sticky-bar vertical" ref={self._refStickyBar}>
            <Calendar
              ref={self._refCalendar}
              boldToday
              onDateSelected={self.onDateChange}
              selectedDate={state.date}
              onMonthChange={self._onCalendarMonthChange}
              dots={self._getCalendarDots(props.visitStore)}
            />

            <div
              className="v grow"
              style={{
                paddingBottom: 8,
                whiteSpace: "nowrap",
                overflow: "hidden",
                display: !haveDrafts ? "none" : "",
              }}
            >
              <div className="cv-line" />
              {haveVisitDrafts && (
                <Button
                  onClick={() =>
                    props.onShowListView && props.onShowListView(DRAFT_TYPE_NEW)
                  }
                  color="primary"
                  className="ha-btn-green cv-drafts-btn-outer"
                  variant="contained"
                >
                  <div className="cv-drafts-btn">
                    <img src="/visit-drafts.png" alt="" />
                    <span className="grow">Saved Visit Drafts</span>
                    <span className="cv-counter">{props.draftsCount}</span>
                  </div>
                </Button>
              )}

              {haveMobileDrafts && (
                <Button
                  onClick={self._showMobileDrafts}
                  color="primary"
                  className="ha-btn-green cv-drafts-btn-outer"
                  variant="contained"
                >
                  <div className="cv-drafts-btn">
                    <img src="/mobile-drafts.png" alt="" />
                    <span className="grow">Mobile Visit Drafts</span>
                    <span className="cv-counter">
                      {props.mobileDraftsCount}
                    </span>
                  </div>
                </Button>
              )}

              {haveEntDrafts && (
                <Button
                  onClick={self._showEnterpriseDrafts}
                  color="primary"
                  className="ha-btn-green cv-drafts-btn-outer"
                  variant="contained"
                >
                  <div className="cv-drafts-btn">
                    <img src="/ent-visit-drafts.png" alt="" />
                    <span className="grow">Enterprise Drafts</span>
                    <span className="cv-counter">{props.entDraftsCount}</span>
                  </div>
                </Button>
              )}

              {haveUpdateDrafts &&
                updateDrafts &&
                updateDrafts.map((d: any) => {
                  return (
                    <Button
                      key={d._id}
                      onClick={() => self._resumeUpdateVisitDrafts(d._id)}
                      color="primary"
                      className="ha-btn-green cv-drafts-btn-outer"
                      variant="contained"
                    >
                      <div className="cv-drafts-btn">
                        <img src="/continue-update.png" alt="" />
                        <div className="grow no-overflow">
                          <div>Continue Updating</div>
                          <div className="cv-cont-up-gray elipsis">
                            Saved on {formatDateDDMMM(d.updated_at)} (by{" "}
                            {d.created_by})
                          </div>
                        </div>
                        <span className="cv-counter">{d.count}</span>
                      </div>
                    </Button>
                  );
                })}

              {havePpDrafts &&
                ppDrafts &&
                ppDrafts.map((d: any) => {
                  return (
                    <div
                      key={d.payment_draft_id}
                      onClick={() =>
                        self._showPendingPaymentDrafts(d.payment_draft_id)
                      }
                      className="ha-btn-green cv-drafts-btn-outer cv-pp-draft-btn"
                    >
                      <div className="cv-drafts-btn">
                        <img src="/self-serve/pending-payment.png" alt="" />
                        <div className="v grow no-overflow">
                          <div>Pending Payment</div>
                          <div>{formatDateDDMMM(d.created_at)}</div>
                          <Div l fs={10} mt={2} color={c7C7C7C}>
                            {d.description}
                          </Div>
                          {!!d.weblink && (
                            <div
                              className="cv-weblink"
                              onClick={(e: any) =>
                                eatClick(e) && self._copyWeblink(d.weblink)
                              }
                            >
                              Copy payment link
                            </div>
                          )}
                        </div>
                        <span className="cv-counter">{d.count}</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        <div
          className="cv-collapse horizontal center hcenter"
          onClick={self._toggleExpand}
        >
          <div className="cv-collapse-arrow" ref={self._refExpandToggle}></div>
        </div>

        <div className="vertical grow no-overflow">
          <Suspense fallback={<LoadingSpinner />}>
            <BigCalendar
              vref={self._refBigCalendar}
              initialDate={state.date}
              title={state.title}
              onScroll={self._onScroll}
              onTapToday={self._onTapToday}
              onNextWeek={self._onNextWeek}
              onPrevWeek={self._onPrevWeek}
            />
          </Suspense>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  ...state,
});

const mapDispatchToProps = (dispatch: any) => ({
  getVisitDrafts: (_id: string) => dispatch(getVisitDrafts(_id)),
  getMobVisitDrafts: (_id: string, onSuccess: any) =>
    dispatch(getMobileVisitDrafts(_id, onSuccess)),
  getEntVisitDrafts: (_id: string, onSuccess: any) =>
    dispatch(getEntVisitDrafts(_id, onSuccess)),
  getCRShortVisits: (_id: string, startDate: Date, endDate: Date) =>
    dispatch(getCRShortVisits(_id, startDate, endDate)),
  getCRFullVisits: (_id: string, startDate: Date, endDate: Date) =>
    dispatch(getCRFullVisits(_id, startDate, endDate)),
});

export default withRouter(
  connect<CalendarViewState, CalendarViewConnectProps>(
    mapStateToProps,
    mapDispatchToProps
  )(CalendarView)
);
